import { useEffect, useState } from 'react';
import { X, BookOpen, Loader2, Layers } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import type { TopicSuggestion } from '@/lib/api-client';

interface TopicScopingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTopic: (focusTopic: string | null) => void;
  topics: TopicSuggestion[];
  totalChunks: number;
  isLoading?: boolean;
}

/**
 * Shown when the attached sources are too large for one lesson: pick a focus
 * topic, or "Use everything".
 *
 * The light surfaces read the same ramp as the FlowSelector options -
 * `--card` at rest, `brand-100` on hover, `brand-200` selected. They were raw
 * Tailwind blues (`bg-blue-50` selected, `hover:bg-muted`, `border-gray-300`),
 * and `bg-blue-50` on a `--card` panel is the same near-invisible pairing that
 * `brand-50` was: the selected row was carried by its ring and text colour
 * while the fill did almost nothing.
 *
 * KNOWN DEBT: this hand-rolls its overlay - a `fixed inset-0` backdrop plus an
 * Escape `useEffect` - which the frontend standards explicitly say not to do.
 * `Modal` in `@ai-tutor/ui` already handles the portal, the focus trap and
 * Escape. The ARIA attributes below are a stopgap; there is still no focus
 * trap, so keyboard focus can leave the dialog while it is open. Porting it
 * onto `Modal` is a behavioural change and wants its own pass.
 */
export function TopicScopingDialog({
  isOpen,
  onClose,
  onSelectTopic,
  topics,
  totalChunks,
  isLoading = false,
}: TopicScopingDialogProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSelect = () => {
    if (selectedIndex === null) {
      onSelectTopic(null); // "Use everything"
    } else {
      onSelectTopic(topics[selectedIndex].title);
    }
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      {/* Backdrop. A <button> rather than a div with an onClick: the click
          handler on a non-interactive element is what tripped
          jsx-a11y/click-events-have-key-events. `aria-hidden` + `tabIndex={-1}`
          keep it out of both the tab order and the accessibility tree, so it
          stays a pure mouse convenience - Escape and the X button are the real
          close affordances. */}
      <button
        type='button'
        aria-hidden='true'
        tabIndex={-1}
        className='absolute inset-0 bg-black/50 backdrop-blur-sm duration-200 animate-in fade-in'
        onClick={onClose}
      />

      {/* Modal. `role=dialog` + `aria-modal` + `aria-labelledby` were all
          missing, so axe reported the panel as an unnamed generic container and
          a screen reader gave no indication a modal had opened. NOTE this is
          still a hand-rolled overlay with no focus trap - see the component
          docstring. */}
      <div
        role='dialog'
        aria-modal='true'
        aria-labelledby='topic-scoping-title'
        className='relative w-full max-w-lg rounded-2xl bg-card shadow-2xl duration-200 animate-in fade-in zoom-in-95'
      >
        {/* Close button */}
        <button
          onClick={onClose}
          // `-m-2 p-3` below `sm`: a 44px touch target whose negative margin
          // keeps the icon centred exactly where the 28px desktop button has it.
          className='absolute right-4 top-4 -m-2 rounded-lg p-3 text-muted-foreground transition-colors hover:bg-muted hover:text-muted-foreground sm:m-0 sm:p-1'
          aria-label='Close dialog'
        >
          <X className='h-5 w-5' />
        </button>

        {/* Header */}
        <div className='border-b border-border p-6 pb-4'>
          <div className='flex items-center gap-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 dark:bg-muted'>
              <Layers className='h-5 w-5 text-brand-600 dark:text-primary' />
            </div>
            <div>
              <h3
                id='topic-scoping-title'
                className='text-lg font-semibold text-foreground'
              >
                Choose a Focus Area
              </h3>
              <p className='text-sm text-muted-foreground'>
                Your sources contain {totalChunks.toLocaleString()} chunks. Pick
                a topic for a focused lesson.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className='max-h-80 overflow-y-auto p-6'>
          {isLoading ? (
            <div className='flex flex-col items-center gap-3 py-8'>
              <Loader2 className='h-6 w-6 animate-spin text-brand-500 dark:text-primary' />
              <p className='text-sm text-muted-foreground'>
                Analyzing your sources...
              </p>
            </div>
          ) : (
            <div className='space-y-2'>
              {topics.map((topic, index) => (
                <button
                  key={topic.title}
                  type='button'
                  onClick={() => setSelectedIndex(index)}
                  className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-all ${
                    selectedIndex === index
                      ? 'border-brand-400 bg-brand-200 ring-1 ring-brand-400 dark:border-primary dark:bg-tint-brand dark:ring-primary'
                      : 'border-border hover:border-brand-300 hover:bg-brand-100 dark:hover:border-primary/40 dark:hover:bg-accent'
                  }`}
                >
                  <BookOpen
                    className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                      selectedIndex === index
                        ? 'text-brand-600 dark:text-primary'
                        : 'text-muted-foreground'
                    }`}
                  />
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        selectedIndex === index
                          ? 'text-brand-700 dark:text-tint-brand-foreground'
                          : 'text-foreground'
                      }`}
                    >
                      {topic.title}
                    </p>
                    {topic.description && (
                      <p className='mt-0.5 text-xs text-muted-foreground'>
                        {topic.description}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Actions. Below `sm` they wrap: "Use everything" and Cancel share a
            row and Generate Lesson takes a full-width row of its own - the
            three in one line put 32px buttons and a 20px text link under a
            thumb. The button group is `contents` there so Cancel and Generate
            become items of THIS row and `basis-full` can break Generate out. */}
        <div className='flex flex-wrap items-center justify-between gap-3 border-t border-border p-6 pt-4 sm:gap-0'>
          <button
            type='button'
            onClick={() => onSelectTopic(null)}
            className='min-h-11 text-sm text-muted-foreground transition-colors hover:text-foreground sm:min-h-0'
          >
            Use everything
          </button>
          <div className='contents sm:flex sm:gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={onClose}
              className='h-11 sm:h-8'
            >
              Cancel
            </Button>
            <Button
              size='sm'
              onClick={handleSelect}
              disabled={isLoading}
              className='h-11 basis-full sm:h-8 sm:basis-auto'
            >
              Generate Lesson
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
